// How many SMS segments a text costs, and in which encoding.
//
// Carriers bill per segment, not per message. A text that fits the GSM-7
// alphabet costs one segment up to 160 characters and 153 per segment beyond
// that (7 go to the concatenation header). One character outside GSM-7 - a
// curly quote pasted from a word processor, an emoji - switches the whole text
// to UCS-2: 70 in one segment, 67 per segment beyond.
//
// GSM-7 EXTENSION characters (^ { } \ [ ] ~ | euro sign, form feed) are sent
// as an escape plus the character, so each counts as 2. A segment boundary
// never splits that pair, and in UCS-2 never splits a surrogate pair (an emoji
// is 2 UTF-16 units), so packing is done character by character rather than by
// dividing a total.
//
// analyze(text, { normalizeQuotes }) returns
//   { encoding: 'GSM-7'|'UCS-2', characters, units, segments, perSegment, nonGsm, text }
//   characters  user-visible characters (code points)
//   units       septets (GSM-7) or UTF-16 code units (UCS-2): what is billed
//   perSegment  the capacity that applied: 160/153 or 70/67
//   nonGsm      the distinct characters that forced UCS-2, in order of first use
//   text        the text analysed (after normalizeQuotes, when asked)
// An empty text is 0 segments.
//
// normalizeQuotes swaps the usual word-processor punctuation for its GSM-7
// equivalent before counting, which is what keeps a pasted message in GSM-7.
//
// MIRROR of server/services/smsSegments.js, which is the reference. Change both
// or neither: both are tested against smsSegments.vectors.json, and the server
// test loads this file too, so a drift fails npm test in server/.

const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅå' +
  'Δ_ΦΓΛΩΠΨΣΘΞÆæßÉ' +
  ' !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§' +
  '¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXTENSION = '\f^{}\\[~]|€';

const BASIC = new Set(GSM_BASIC);
const EXTENSION = new Set(GSM_EXTENSION);

const QUOTE_MAP = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'", '′': "'",
  '“': '"', '”': '"', '„': '"', '‟': '"', '″': '"',
  '–': '-', '—': '-', '‒': '-', '−': '-',
  '…': '...', ' ': ' ', ' ': ' ', ' ': ' ',
};

function normalize(text) {
  return Array.from(text).map((ch) => (QUOTE_MAP[ch] !== undefined ? QUOTE_MAP[ch] : ch)).join('');
}

// Packs per-character unit costs into segments of `size` without splitting a
// character across two segments.
function pack(costs, size) {
  let segments = 1;
  let used = 0;
  for (const cost of costs) {
    if (used + cost > size) {
      segments += 1;
      used = 0;
    }
    used += cost;
  }
  return segments;
}

function analyze(input, options) {
  const opts = options || {};
  const raw = input === null || input === undefined ? '' : String(input);
  const text = opts.normalizeQuotes ? normalize(raw) : raw;
  const chars = Array.from(text);

  const nonGsm = [];
  for (const ch of chars) {
    if (!BASIC.has(ch) && !EXTENSION.has(ch) && !nonGsm.includes(ch)) nonGsm.push(ch);
  }
  const gsm = nonGsm.length === 0;

  const costs = chars.map((ch) => (gsm ? (EXTENSION.has(ch) ? 2 : 1) : ch.length));
  const units = costs.reduce((a, b) => a + b, 0);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;

  let segments;
  if (units === 0) segments = 0;
  else if (units <= single) segments = 1;
  else segments = pack(costs, multi);

  return {
    encoding: gsm ? 'GSM-7' : 'UCS-2',
    characters: chars.length,
    units,
    segments,
    perSegment: segments > 1 ? multi : single,
    nonGsm,
    text,
  };
}

export { analyze, normalize, GSM_BASIC, GSM_EXTENSION };
